import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, ArrowLeft, DollarSign, Plus, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ReportMarkdownRenderer } from "@/components/ReportMarkdownRenderer";
import { format } from "date-fns";

export default function SalaryIntelReports() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState<any>(null);

  useEffect(() => {
    if (!user) { navigate("/auth"); return; }
    fetchReports();
  }, [user]);

  const fetchReports = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('salary_intel_reports' as any)
      .select('*')
      .eq('user_id', user?.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching salary intel reports:', error);
    } else {
      setReports(data || []);
    }
    setLoading(false);
  };

  const deleteReport = async (id: string) => {
    const { error } = await supabase.from('salary_intel_reports' as any).delete().eq('id', id);
    if (error) {
      toast({ title: "Error", description: "Failed to delete report", variant: "destructive" });
    } else {
      toast({ title: "Deleted", description: "Report removed" });
      setReports(prev => prev.filter(r => (r as any).id !== id));
      if (selectedReport && (selectedReport as any).id === id) setSelectedReport(null);
    }
  };

  if (selectedReport) {
    return (
      <AppLayout currentPage="salary-intel-reports">
        <div className="container mx-auto py-8 px-4 max-w-4xl">
          <div className="flex items-center gap-4 mb-6">
            <Button variant="outline" onClick={() => setSelectedReport(null)}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Reports
            </Button>
          </div>
          <h1 className="text-2xl font-bold mb-1">Salary & Benefits Intelligence Report</h1>
          <p className="text-muted-foreground mb-6">
            {(selectedReport as any).company_name} — {(selectedReport as any).job_title} • {format(new Date((selectedReport as any).created_at), 'MMM d, yyyy')}
          </p>
          <Card>
            <CardContent className="p-6">
              {(selectedReport as any).report ? (
                <ReportMarkdownRenderer content={(selectedReport as any).report} />
              ) : (
                <p className="text-muted-foreground">No report content available.</p>
              )}
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout currentPage="salary-intel-reports">
      <div className="container mx-auto py-8 px-4 max-w-5xl">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/jobintel-360-reports')}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Salary & Benefits Intelligence Reports</h1>
              <p className="text-muted-foreground">Your saved compensation research</p>
            </div>
          </div>
          <Button onClick={() => navigate('/salary-intel')} className="bg-green-600 hover:bg-green-700">
            <Plus className="w-4 h-4 mr-2" /> New Report
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-green-600" /></div>
        ) : reports.length === 0 ? (
          <Card className="border-dashed border-2">
            <CardContent className="p-12 text-center">
              <DollarSign className="w-12 h-12 text-green-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No reports yet</h3>
              <p className="text-muted-foreground mb-4">Generate your first Salary & Benefits Intelligence report</p>
              <Button onClick={() => navigate('/salary-intel')} className="bg-green-600 hover:bg-green-700">
                <Plus className="w-4 h-4 mr-2" /> Generate Report
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {reports.map((r: any) => (
              <Card
                key={r.id}
                className="cursor-pointer hover:shadow-md transition-shadow border border-green-200 hover:border-green-400"
                onClick={() => setSelectedReport(r)}
              >
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <DollarSign className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">
                        {r.company_name || 'Unknown Company'} — {r.job_title || 'Unknown Role'}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(r.created_at), 'MMM d, yyyy h:mm a')}
                        {r.location && ` • ${r.location}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={r.status === 'completed' ? 'default' : r.status === 'failed' ? 'destructive' : 'secondary'}>
                      {r.status}
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); deleteReport(r.id); }}
                    >
                      <Trash2 className="w-4 h-4 text-muted-foreground" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
